import React, { useState } from 'react';
import { Box, TextField, Button, IconButton, InputAdornment, Typography } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import GolfCourseIcon from '@mui/icons-material/GolfCourse';
import { useLogin, useNotify } from 'react-admin';
import { golfosBrand } from '../../config/branding';
import { GolfOSMark } from './GolfOSMark';

const GolfOSLoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const login = useLogin();
  const notify = useNotify();

  const buttonGradient = golfosBrand.theme.buttonGradient;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    login({ email, password }).catch(() => notify('Invalid email or password'));
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        width: '100%',
        maxWidth: 380,
      }}
    >
      <TextField
        label="Work email"
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        required
        fullWidth
        autoComplete="email"
        variant="outlined"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.95)',
          },
        }}
      />
      <TextField
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={e => setPassword(e.target.value)}
        required
        fullWidth
        autoComplete="current-password"
        variant="outlined"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.95)',
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
                aria-label="toggle password visibility"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        sx={{
          mt: 1,
          py: 1.5,
          borderRadius: 2,
          fontWeight: 700,
          fontSize: '1rem',
          textTransform: 'none',
          boxShadow: '0 8px 24px rgba(45, 106, 47, 0.35)',
          background: buttonGradient,
          '&:hover': {
            background: buttonGradient,
            opacity: 0.95,
            boxShadow: '0 10px 28px rgba(45, 106, 47, 0.42)',
          },
        }}
      >
        Sign in to GolfOS
      </Button>
    </Box>
  );
};

export const GolfOSLoginPage = () => {
  const bg = golfosBrand.theme.headerGradient;
  const accent = golfosBrand.colors.accent;

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        width: '100%',
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      {/* Left: brand panel */}
      <Box
        sx={{
          flex: { xs: 'none', md: '0 0 44%' },
          minHeight: { xs: 220, md: '100vh' },
          position: 'relative',
          overflow: 'hidden',
          background: bg,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          px: { xs: 3, md: 6 },
          py: { xs: 4, md: 8 },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.07,
            backgroundImage: `repeating-linear-gradient(
              90deg,
              transparent,
              transparent 32px,
              rgba(255,255,255,0.4) 32px,
              rgba(255,255,255,0.4) 33px
            )`,
            pointerEvents: 'none',
          }}
        />
        <GolfCourseIcon
          sx={{
            position: 'absolute',
            right: '-8%',
            bottom: '-10%',
            fontSize: 320,
            color: 'rgba(255,255,255,0.06)',
            pointerEvents: 'none',
          }}
        />
        <Box sx={{ position: 'relative', zIndex: 1, mb: 3 }}>
          <GolfOSMark height={42} variant="light" />
        </Box>
        <Typography
          variant="overline"
          sx={{
            color: accent,
            letterSpacing: '0.2em',
            fontWeight: 700,
            position: 'relative',
            zIndex: 1,
          }}
        >
          Club operations
        </Typography>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            color: '#fff',
            fontWeight: 800,
            lineHeight: 1.15,
            mt: 1,
            mb: 2,
            fontSize: { xs: '1.85rem', md: '2.35rem' },
            position: 'relative',
            zIndex: 1,
          }}
        >
          Everything your course runs on—one login.
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255,255,255,0.88)',
            maxWidth: 400,
            lineHeight: 1.6,
            position: 'relative',
            zIndex: 1,
          }}
        >
          Tee sheet adjacency, service requests, fleet, and member-facing workflows in a single workspace
          built for superintendents, pros, and ops teams.
        </Typography>
      </Box>

      {/* Right: form */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: { xs: 4, md: 6 },
          background: 'linear-gradient(180deg, #f8fcf8 0%, #eef5ee 100%)',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 420,
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 24px 80px rgba(26, 63, 27, 0.12), 0 0 0 1px rgba(45, 106, 47, 0.08)',
          }}
        >
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <GolfOSMark height={44} variant="dark" />
          </Box>
          <Typography variant="h5" fontWeight={700} color="#1A3F1B" sx={{ mb: 0.5, textAlign: 'center' }}>
            Welcome back
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            Sign in to manage your club workspace
          </Typography>
          <GolfOSLoginForm />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 3, opacity: 0.85 }}>
          GolfOS · Secure sign-in
        </Typography>
      </Box>
    </Box>
  );
};
